import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { TaxonomyOption } from '../hooks/useProfileTaxonomy';
import { useTaxonomyLabel } from '../hooks/useProfileTaxonomy';

interface TagSelectorProps {
  options: TaxonomyOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Dropdown (met chips) voor veel opties, checkboxes voor korte lijsten */
  variant?: 'dropdown' | 'checkbox';
  /** Maximum aantal selecties; verder selecteren wordt geblokkeerd */
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Meervoudige selectie van taxonomie-waarden (specialisme-tags, doelgroep-
 * niveaus, branche-affiniteiten). FO stap 4 & 5.
 */
export default function TagSelector({
  options,
  value,
  onChange,
  variant = 'dropdown',
  max,
  placeholder,
  disabled = false,
  id,
}: TagSelectorProps) {
  const { t } = useTranslation('profile');
  const label = useTaxonomyLabel();

  const atMax = max !== undefined && value.length >= max;

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else if (!atMax) {
      onChange([...value, optionValue]);
    }
  };

  if (variant === 'checkbox') {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const checked = value.includes(option.value);
          return (
            <label
              key={option.value}
              className={`flex items-center gap-3 rounded-lg border p-3 text-sm transition-colors ${
                checked ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-300 hover:border-gray-400'
              } ${disabled || (!checked && atMax) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={checked}
                disabled={disabled || (!checked && atMax)}
                onChange={() => toggle(option.value)}
              />
              <span>{label(option)}</span>
            </label>
          );
        })}
      </div>
    );
  }

  const remaining = options.filter((option) => !value.includes(option.value));

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {value.map((selected) => {
            const option = options.find((o) => o.value === selected);
            return (
              <span
                key={selected}
                className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
              >
                {option ? label(option) : selected}
                <button
                  type="button"
                  className="ml-2 text-blue-600 hover:text-blue-800"
                  aria-label={t('button.removeTag', { defaultValue: 'Verwijderen' })}
                  onClick={() => onChange(value.filter((v) => v !== selected))}
                  disabled={disabled}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <select
        id={id}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        value=""
        disabled={disabled || atMax || remaining.length === 0}
        onChange={(event) => {
          if (event.target.value) toggle(event.target.value);
        }}
      >
        <option value="">
          {atMax
            ? t('tags.maxReached', { count: max, defaultValue: 'Maximum bereikt' })
            : placeholder || t('tags.select', { defaultValue: 'Selecteer…' })}
        </option>
        {remaining.map((option) => (
          <option key={option.value} value={option.value}>
            {label(option)}
          </option>
        ))}
      </select>

      {max !== undefined && (
        <p className="mt-1 text-xs text-gray-500">
          {t('tags.counter', {
            count: value.length,
            max,
            defaultValue: `${value.length} van ${max} geselecteerd`,
          })}
        </p>
      )}
    </div>
  );
}
